import { State, process } from '@progress/kendo-data-query';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';
import { GridPagerSettings } from '@progress/kendo-react-grid';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useWindowSize from 'src/hooks/useWindowSize';
import LoadingPanel from './LoadingPanel';
import CardGridHeader from './catalog/CardGridHeader';

const generalTable = (
  param: string,
  t: (key: string, options?: Record<string, string>) => string
) => {
  const itemsLabel = t(param);
  return {
    grid: {
      pagerItemsPerPage: t('pagerItemsPerPage', { items: itemsLabel }),
      pagerInfo: t('pagerInfo', { items: itemsLabel }),
      isNullOrEmpty: t('Is null or empty'),
      notIsNullOrEmpty: t('Is not null or empty'),
    },
    // Pager from @progress/kendo-react-data-tools uses pager.* keys (not grid.*)
    pager: {
      itemsPerPage: t('pagerItemsPerPage', { items: itemsLabel }),
      info: t('pagerInfo', { items: itemsLabel }),
    },
  };
};

interface CardGridWrapperProps<T> {
  dataSource: T[];
  renderItem: (item: T, index: number) => ReactNode;
  dataState?: State;
  onDataStateChange?: (dataState: State) => void;
  pageable?: boolean | GridPagerSettings;
  loading?: boolean;
  className?: string;
  tableTextId?: string;
  noRecordText?: string;
  gap?: string;
  height?: string;
  stickyPager?: boolean;
  total?: number; // Total count for server-side pagination
  id?: string;
  headerChildren?: ReactNode;
  subHeaderChildren?: ReactNode;
  minColumnWidth?: number;
  gridClassName?: string;
}

const CardGridWrapper = <T,>({
  dataSource,
  renderItem,
  dataState: externalDataState,
  onDataStateChange,
  pageable = true,
  loading = false,
  className = '',
  tableTextId = 'Items',
  noRecordText,
  gap = 'gap-4',
  height,
  stickyPager = false,
  total: externalTotal,
  id = 'card-grid-wrapper',
  headerChildren,
  subHeaderChildren,
  minColumnWidth = 235,
  gridClassName,
}: CardGridWrapperProps<T>) => {
  const { t, i18n } = useTranslation();
  const resolvedNoRecordText = noRecordText || t('No items to display');
  const [internalDataState, setInternalDataState] = useState<State>({
    skip: 0,
    take: 25,
  });

  const dataState = externalDataState || internalDataState;

  // Create a wrapper function that handles both internal and external state updates
  const setDataState = useCallback(
    (newState: State) => {
      if (onDataStateChange) {
        // External handler expects State object directly
        onDataStateChange(newState);
      } else {
        // Internal state setter
        setInternalDataState(newState);
      }
    },
    [onDataStateChange]
  );
  // Process data with pagination (client-side) or use pre-paginated data (server-side)
  const processedData = useMemo(() => {
    // If total is provided, data is already paginated server-side
    if (externalTotal !== undefined) {
      // Use dataSource even if empty (might be loading with previous data)
      return { data: dataSource || [], total: externalTotal };
    }
    // Otherwise, do client-side pagination
    if (!dataSource || dataSource.length === 0) {
      return { data: [], total: 0 };
    }
    return process(dataSource, dataState);
  }, [dataSource, dataState, externalTotal]);

  // Determine pager settings
  const pagerSettings: GridPagerSettings | false = useMemo(() => {
    if (typeof pageable === 'boolean') {
      return pageable
        ? {
            buttonCount: 4,
            pageSizes: [10, 25, 50, 100],
          }
        : false;
    }
    return pageable;
  }, [pageable]);

  const localizationLanguage = `${tableTextId}-${i18n.language}`;
  useEffect(() => {
    if (tableTextId) {
      loadMessages(generalTable(tableTextId, t), localizationLanguage);
    }
  }, [tableTextId, i18n.language, localizationLanguage, t]);

  // If loading, show the grid with loader (pagination will be visible)
  // If not loading and no data, show "No records available"
  // Otherwise show the grid with data

  return (
    <LocalizationProvider language={localizationLanguage}>
      <CardGridContent
        processedData={processedData}
        dataState={dataState}
        setDataState={setDataState}
        renderItem={renderItem}
        pagerSettings={pagerSettings}
        className={className}
        gap={gap}
        height={height}
        stickyPager={stickyPager}
        loading={loading}
        noRecordText={resolvedNoRecordText}
        hasData={dataSource && dataSource.length > 0}
        id={id}
        headerChildren={headerChildren}
        subHeaderChildren={subHeaderChildren}
        minColumnWidth={minColumnWidth}
        gridClassName={gridClassName}
      />
    </LocalizationProvider>
  );
};

interface CardGridContentProps<T> {
  processedData: { data: T[]; total: number };
  dataState: State;
  setDataState: (state: State) => void;
  renderItem: (item: T, index: number) => ReactNode;
  pagerSettings: GridPagerSettings | false;
  className: string;
  gap: string;
  height?: string;
  stickyPager: boolean;
  loading: boolean;
  noRecordText: string;
  hasData: boolean;
  id: string;
  headerChildren?: ReactNode;
  subHeaderChildren?: ReactNode;
  minColumnWidth: number;
  gridClassName?: string;
}

const CardGridContent = <T,>({
  processedData,
  dataState,
  setDataState,
  renderItem,
  pagerSettings,
  className,
  gap,
  height,
  stickyPager,
  loading,
  noRecordText,
  hasData,
  id,
  headerChildren,
  subHeaderChildren,
  minColumnWidth,
  gridClassName,
}: CardGridContentProps<T>) => {
  // Use ref to always get the latest dataState without recreating the callback
  const dataStateRef = useRef(dataState);
  useEffect(() => {
    dataStateRef.current = dataState;
  }, [dataState]);

  const handlePageChange = useCallback(
    (event: PageChangeEvent) => {
      // The Pager component from kendo-react-data-tools passes event with skip and take
      // Based on Kendo documentation, the event structure is: { skip: number, take: number }
      const eventAny = event as any;

      // Extract skip and take - the Pager passes them directly on the event object
      const skip = eventAny.skip ?? eventAny.page?.skip ?? 0;
      const take = eventAny.take ?? eventAny.page?.take ?? dataStateRef.current.take ?? 25;

      const newState: State = {
        ...dataStateRef.current,
        skip,
        take,
        // Preserve sort if it exists
        ...(dataStateRef.current.sort && { sort: dataStateRef.current.sort }),
      };
      setDataState(newState);
    },
    [setDataState]
  );

  const { width } = useWindowSize();
  const useFlex = useMemo(
    () => !gridClassName && minColumnWidth * processedData.data.length < width,
    [gridClassName, minColumnWidth, processedData.data.length, width]
  );

  return (
    <>
      <div
        className={`card-grid-wrapper flex flex-col relative ${className}`}
        style={{ height: height || '100%', zIndex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <div
          className={`flex-shrink-0 bg-white border-t border-gray-200 shadow-sm`}
          style={{
            zIndex: 40,
            pointerEvents: 'auto',
            position: 'sticky',
            top: 0,
          }}
        >
          {headerChildren ?? <CardGridHeader />}
        </div>
        {subHeaderChildren}
        <div
          className={`flex-1 overflow-auto relative min-h-0 ${subHeaderChildren ? 'pb-4' : 'py-4'}`}
          style={{ zIndex: 0 }}
        >
          {!hasData && !loading ? (
            <div className="flex justify-center items-center w-full">
              <p className="text-black font-[400] text-[13px]">{noRecordText}</p>
            </div>
          ) : (
            <div
              className={clsx(
                gridClassName ?? (useFlex ? 'flex flex-wrap' : 'grid'),
                !gridClassName && gap
              )}
              {...(!gridClassName &&
                !useFlex && {
                  style: {
                    gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
                  },
                })}
            >
              {processedData.data.map((item: T, index: number) => renderItem(item, index))}
            </div>
          )}
        </div>
        {pagerSettings && (
          <div
            className={`flex-shrink-0 ${
              stickyPager ? 'bg-white border-t border-gray-200 shadow-sm' : ''
            }`}
            style={{
              zIndex: stickyPager ? 30 : 1,
              pointerEvents: 'auto',
              ...(stickyPager && {
                position: 'sticky',
                bottom: 0,
              }),
            }}
          >
            <Pager
              skip={dataState.skip ?? 0}
              take={dataState.take ?? 25}
              total={processedData.total}
              pageSizes={pagerSettings.pageSizes as number[]}
              buttonCount={pagerSettings.buttonCount}
              onPageChange={handlePageChange}
              previousNext={true}
              info={true}
            />
          </div>
        )}
      </div>
      {loading && <LoadingPanel id={id} />}
    </>
  );
};

export default CardGridWrapper;
